import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Formik } from 'formik';
import { Col } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import { RootState } from 'store';
import { setLeadData } from 'leadData/leadSlice';
import {
  ContractFlowClient,
  Offer,
  Term,
} from 'v3/api/client/ContractFlowClient';
import { getErrorObject, handleErrorMessage } from 'v3/utils/errorHandler';
import SidebarBox from 'v3/components/SidebarBox';
import Field from 'v3/components/Field';
import Steps from 'v3/components/Steps';
import Button from 'v3/components/Button';
import OfferBox from 'v3/components/OfferBox';
import LoadingSpinner from 'v3/components/LoadingSpinner';
import ErrorFocus from 'v3/components/ErrorFocus';
import MultiEmailsField from 'v3/components/MultiEmails';
import CopyCode from 'v3/components/CopyCode';

import BlinkIco from 'v3/assets/svg/ico-savings-blink.svg';
import General from 'v3/templates/General';
import LoadingState from 'v3/components/LoadingState';

import * as S from './styles';
import { useIsRealEstateClubId } from 'v3/hooks/useIsRealEstateClubId';

const reviewOfferSchema = Yup.object().shape({
  invitationCode: Yup.string(),
});
const inviteFriendsSchema = Yup.object().shape({
  emails: Yup.string()
    .required('This field is required')
    .test('max-emails', 'You can only invite up to 5 friends', value => {
      if (!value) return true;
      const emailCount = value.split(',').length;
      return emailCount <= 5;
    }),
});

const ReviewOfferWaitlist = () => {
  const { leadData, subscribedOnWaitingList } = useSelector(
    (state: RootState) => state.lead
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isLoading2, setIsLoading2] = useState(false);
  const [waitingListUserId, setWaitingListUserId] = useState<number>(0);
  const [invitationReferralCode, setInvitationReferralCode] =
    useState<string>('');

  const handleSubscribeWaitlist = useCallback(async () => {
    const { clubId } = leadData;
    const body = {
      firstName: leadData.firstName,
      lastName: leadData.lastName,
      email: leadData.email,
      zipCode: leadData.address.postalCode,
      group: (clubId || leadData.group.id).toString(),
      referralCode: invitationReferralCode,
    };

    const { data, error } = await ContractFlowClient().subscribeToWaitingList({
      body,
    });

    const emailErrors = getErrorObject(error)?.errorList?.email;
    const isEmailAlreadySubscribedError = Boolean(
      emailErrors?.[0]?.toLowerCase().includes('waitlist with this email already exists')
    );

    if (data) {
      setWaitingListUserId(data.id);
      dispatch(setLeadData({ subscribedOnWaitingList: true }));
    }

    // Only a real, unexpected failure is worth reporting: the capture used to run on the
    // successful subscription too, and threw reading `error.response` of undefined.
    if (error && !isEmailAlreadySubscribedError) {
      Sentry.captureException(getErrorObject(error)?.errorList || error);
      toast.warn('You already are on the waiting list!');
    }
  }, [leadData.email, leadData.firstName, leadData.lastName, leadData.group.id, leadData.address.postalCode, invitationReferralCode]);

  const isRealEstateClubId = useIsRealEstateClubId()

  useEffect(() => {
    // waitlist is automatically subscribed to waiting list for real estate or auto clubIds
    // if (!isRealEstateClubId) {
    //   console.log('not real estate clubId', leadData.clubId, 'skipping waitlist auto subscription')
    //   return;
    // }

      console.log("Handle effect for waitlist auto subscription")
      // make sure the leadData.address.postalCode is not null
      if ([leadData.email, leadData.firstName, leadData.lastName, leadData.group.id, leadData.address.postalCode].every(field => !!field)) {
        async function send() {
          setIsLoading(true);
          await handleSubscribeWaitlist();
          setIsLoading(false);
        }

        send();

      } else {
        console.log('Required parameter is missing', leadData)
      }

      // setWaitingListUserId((leadData as any)?.uuid);
      // dispatch(setLeadData({ subscribedOnWaitingList: true }));

  }, [leadData.email, leadData.firstName, leadData.lastName, leadData.group.id, leadData.address.postalCode, handleSubscribeWaitlist]);

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const cfUuid = new URLSearchParams(window.location.search).get('cfUuid');

  useEffect(() => {
    if (!cfUuid) {
      navigate('/', {
        replace: true,
      });
    }
  }, []);

  // useEffect(() => {
  //   const getDealChances = async () => {
  //     const { data } = await ContractFlowClient().getDealChances({
  //       vehiclePrice: leadData.dealValue,
  //       term: leadData.term,
  //       creditScore: leadData.creditScore,
  //       downPayment: leadData.contractFlowDownPayment,
  //       halfPaymentSelected: leadData.contractFlowHalfPaymentSelected,
  //     });

  //     if (data) {
  //       setRange(data.userChanceRange);
  //     }
  //   };

  //   // if it checks for leadData.creditScore it's currently giving nulls for some reason and it breaks the review offer step
  //   if (leadData.dealValue && leadData.term) {
  //     getDealChances();
  //   }
  // }, [
  //   leadData.dealValue,
  //   leadData.term,
  //   leadData.creditScore,
  //   leadData.contractFlowDownPayment,
  //   leadData.contractFlowHalfPaymentSelected,
  // ]);

  // useEffect(() => {
  //   const calculateFees = async (term: string) => {
  //     setIsOfferLoading(true);

  //     const { data } = await ContractFlowClient().calculateFee(
  //       cfUuid as string,
  //       leadData.term,
  //       leadData.contractFlowDownPayment
  //     );

  //     if (data) {
  //       const { monthlyPayment, managementFee } = data;

  //       if (leadData.contractFlowHalfPaymentSelected) {
  //         const { data: halfPaymentData } =
  //           await ContractFlowClient().getHalfPayment(
  //             managementFee,
  //             monthlyPayment
  //           );

  //         if (halfPaymentData) {
  //           setHalfPayment(halfPaymentData.data.halfPayment);
  //         }
  //       }

  //       setOffer({ ...data });
  //     }

  //     setIsOfferLoading(false);
  //   };

  //   // no offer is needed in ReviewOffer WAITLIST page anymore
  //   // calculateFees(leadData.term);
  // }, [leadData.term, leadData.contractFlowHalfPaymentSelected, cfUuid, leadData.contractFlowDownPayment]);

  // useEffect(() => {
  //   async function generateUserSpecificToken(
  //     firstName: string,
  //     lastName: string,
  //     email: string
  //   ) {
  //     const userData = `${firstName}:${lastName}:${email}:${Date.now()}`;

  //     const encoder = new TextEncoder();
  //     const data = encoder.encode(userData);

  //     const salt = crypto.getRandomValues(new Uint8Array(16));

  //     const generatedHash = crypto.subtle
  //       .digest('SHA-256', new Uint8Array([...salt, ...data]))
  //       .then((hash) => {
  //         const hashArray = Array.from(new Uint8Array(hash));
  //         const hashHex = hashArray
  //           .map((b) => b.toString(16).padStart(2, '0'))
  //           .join('');
  //         return hashHex.slice(0, 6);
  //       });

  //     return await generatedHash;
  //   }

  //   async function generateHash() {
  //     if (leadData.firstName && leadData.lastName && leadData.email) {
  //       try {
  //         const randomHash = await generateUserSpecificToken(
  //           leadData.firstName,
  //           leadData.lastName,
  //           leadData.email
  //         );
  //         const referralCode = `${leadData.firstName.replace(/\s/g, "").toLowerCase()}${randomHash}`;
  //         console.log({referralCode})
  //         setInvitationReferralCode(referralCode);
  //       } catch (error) {
  //         console.error('Error generating hash:', error);
  //       }
  //     }
  //   }

  //   generateHash();
  // }, [leadData.firstName, leadData.lastName, leadData.email]);

  const copyReferralToClipboard = () => {
    navigator.clipboard.writeText(invitationReferralCode);
    toast.success('Copied');
  };

  const handleInviteToWaitingList = async (emails: string[], id: number) => {
    const body = {
      inviteEmails: emails,
    };

    const error = await ContractFlowClient().inviteEmailsToWaitingList({
      body,
      id,
    });

    if (!error) {
      toast.success('Great! Your friends have been invited to join Savings Club! 🎉', {
        toastId: 'invite-friends-success',
      });
    } else if (error) {
      handleErrorMessage(error?.error);
    }
  };

  return (
    <General>
      <Col xs={24} xl={12}>
        <S.Wrapper>
          <SidebarBox variant="secondary" as="section" >
            {(!subscribedOnWaitingList) ? (
              <>
                <S.LoadingWrapper>
                  <LoadingState>
                    <S.LoadingContent>
                      Just a moment.
                      <br />
                      We're adding you to our exclusive waitlist...
                    </S.LoadingContent>
                  </LoadingState>
                </S.LoadingWrapper>
              </>
            ) : (
              <>
                {/* offer box is no longer displayed in the ReviewOffer WAITLIST page */}
                {/* {!isRealEstateClubId ?
                  <S.OfferBox>
                    <OfferBox
                      loading={isOfferLoading}
                      monthlyPayment={offer.monthlyPayment}
                      halfPayment={halfPayment}
                      amount={leadData.dealValue}
                      eapr={offer.apy}
                      term={Number(leadData.term) as Term}
                      paymentOption={
                        leadData.contractFlowHalfPaymentSelected ? 'half' : 'full'
                      }
                      downPayment={leadData.contractFlowDownPayment}
                      range={range}
                      referralCode={leadData.referralCode}
                      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                      referralCodeSavings={`$${offer.referralCodeSavings?.toFixed(
                        2
                      )}`}
                    />
                  </S.OfferBox>
                  : null} */}

                {!subscribedOnWaitingList ? (
                  <Formik
                    isInitialValid={false}
                    initialValues={{
                      invitationCode: '',
                    }}
                    validationSchema={reviewOfferSchema}
                    onSubmit={async () => {
                      setIsLoading(true);
                      await handleSubscribeWaitlist();
                      setIsLoading(false);
                    }}
                  >
                    {({ errors, touched, setFieldError, values }) => (
                      <S.SubscribeContent>
                        <Form>
                          <S.SubscribeSection>
                            <S.SubscribeIco src={BlinkIco} alt="" />

                            <S.SubscribeTitle>
                              Hi, {leadData.firstName}!
                            </S.SubscribeTitle>
                            <S.SubscribeDescription>
                              To continue,{' '}
                              <span>please insert your invitation code.</span>
                            </S.SubscribeDescription>

                            <Field
                              name="invitationCode"
                              placeholder="Invitation Code"
                              rightContent={
                                <Button
                                  variant="invert"
                                  size="small"
                                  type="button"
                                  onClick={async () => {
                                    // const { data } =
                                    //   await ContractFlowClient().getReferralCode(
                                    //     values.invitationCode
                                    //   );

                                    // if (data?.valid) {
                                    //   navigate(
                                    //     `/?cfUuid=${leadData.contractFlowToken}`,
                                    //     {
                                    //       replace: true,
                                    //     }
                                    //   );
                                    // } else {
                                    //   setFieldError(
                                    //     'invitationCode',
                                    //     'Invalid Invitation Code'
                                    //   );
                                    // }

                                    /* TODO: For founders invite logics */
                                    const { error } =
                                      await ContractFlowClient().applyReferral(
                                        leadData.contractFlowToken,
                                        values.invitationCode
                                      );

                                    if (!error) {
                                      navigate(
                                        `/?cfUuid=${leadData.contractFlowToken}`,
                                        {
                                          replace: true,
                                        }
                                      );
                                    } else {
                                      setFieldError(
                                        'invitationCode',
                                        'Invalid Invitation Code'
                                      );
                                    }
                                    /* TODO: For founders invite logics */
                                  }}
                                >
                                  Apply
                                </Button>
                              }
                              feedbackMessage={errors.invitationCode}
                              isValid={
                                !(
                                  touched.invitationCode &&
                                  errors.invitationCode
                                )
                              }
                            />

                            <S.SubscribeText>
                              Don't have an Invitation Code?
                            </S.SubscribeText>

                            <Button
                              variant="brandPrimary"
                              disabled={isLoading}
                              type="submit"
                            >
                              {isLoading ? (
                                <LoadingSpinner />
                              ) : (
                                'Join the Waiting List!'
                              )}
                            </Button>
                          </S.SubscribeSection>
                          <ErrorFocus />
                        </Form>
                      </S.SubscribeContent>
                    )}
                  </Formik>
                ) : (
                  <Formik
                    isInitialValid={false}
                    initialValues={{
                      emails: '',
                    }}
                    validationSchema={inviteFriendsSchema}
                    onSubmit={async (values) => {
                      setIsLoading2(true);

                      const emails = values.emails.split(',');

                      if (values.emails) {
                        await handleInviteToWaitingList(
                          emails,
                          waitingListUserId
                        );
                      }

                      // if set to true, it cleans up the email field which breaks the UX
                      // dispatch(setLeadData({ inviteSent: true }));

                      setTimeout(() => {
                        setIsLoading2(false);
                      }, 1500);
                    }}
                  >
                    {({ errors, touched }) => (
                      <S.SubscribeContent noTopPadding={true}>
                        <Form>
                          <S.InviteSection>
                            <S.SubscribeTitle>
                              Thank you for joining our <S.BrandedText>exclusive waitlist!</S.BrandedText>
                            </S.SubscribeTitle>
                            <S.InviteDescription>
                              Your spot has been secured. We'll notify you as soon as your personalized offer becomes available.
                            </S.InviteDescription>

                            <S.Hr />

                            <S.EmailsSection>
                              <S.EmailsLabel>
                                <S.EmailTitle>You can invite up to 5 friends to join Savings Club. </S.EmailTitle>
                                <S.EmailDescription>
                                The more friends you invite, the higher your priority on our waitlist
                                </S.EmailDescription>
                              </S.EmailsLabel>
                              <MultiEmailsField
                                autoFocus={false}
                                maxEmails={5}
                                name="emails"
                                isValid={!(touched.emails && errors.emails)}
                                feedbackMessage={errors.emails}
                              />

                              <Button
                                variant="brandPrimary"
                                disabled={isLoading2}
                                type="submit"
                              >
                                {isLoading2 ? (
                                  <LoadingSpinner />
                                ) : (
                                  'Invite Friends!'
                                )}
                              </Button>
                            </S.EmailsSection>
                          </S.InviteSection>


                          {/* <CopyCode
                              code={invitationReferralCode}
                              variant="secondary"
                              onClick={copyReferralToClipboard}
                            /> */}

                          {/* <S.ReferralSection>
                            <S.ReferralContent>
                              <S.ReferralText>
                                <strong>Personal Refferal Code</strong>
                              </S.ReferralText>
                              <S.ReferralText>
                                Share with your friends.
                              </S.ReferralText>
                            </S.ReferralContent>

                            <CopyCode
                              code={invitationReferralCode}
                              variant="secondary"
                              onClick={copyReferralToClipboard}
                            />
                          </S.ReferralSection> */}
                          <ErrorFocus />
                        </Form>
                      </S.SubscribeContent>
                    )}
                  </Formik>
                )}
              </>
            )}
          </SidebarBox>
        </S.Wrapper>
      </Col>
    </General>
  );
};

export default ReviewOfferWaitlist;
